/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import {
  Autocomplete,
  Box,
  Divider,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectHotelDropdownList,
  selectHotelGroupDropdownList,
  selectMergedHotelList,
  selectIsDemoUser,
  selectIsDemoModeActivated,
} from 'modules/dashboard/selectors';
import { useEffect, useState } from 'react';
import { selectAuthUser } from 'modules/common/auth/selectors';
import { getHotelDemoModeFocusOnValue } from 'modules/dashboard/functions';
import { ADMIN_ROLE, SUPER_ADMIN_ROLE } from 'modules/common/constants/roles';
import { Apartment } from '@mui/icons-material';
import {
  selectFavoriteScenarios,
  selectScenarios,
  selectSearchData,
  selectSelectedScenario,
} from '../../selectors';
import { searchActions } from '../../slice';

/**
 * SearchHotelDropdown
 *
 * Displays an Autocomplete dropdown for selecting hotels.
 * Merges, modifies, and groups hotel options based on favorites, predefined lists,
 *
 * @param {Object} props.filterData - Filter info for the selected scenario and visual.
 * @param {Function} props.handleClose - Callback to close dropdown after selection.
 * @returns {JSX.Element} Hotel selection Autocomplete
 */
const SearchHotelDropdown = ({ filterData, found, handleClose }) => {
  const hotelList = useSelector(selectHotelDropdownList);
  const groupsList = useSelector(selectHotelGroupDropdownList);
  const authUser = useSelector(selectAuthUser);
  const mergedHotelList = useSelector(selectMergedHotelList);
  const isDemoUser = useSelector(selectIsDemoUser);
  const isDemoModeActivated = useSelector(selectIsDemoModeActivated);
  const [modifiedList, setModifiedList] = useState([]);
  const [addHotelGroupList, setAddHotelGroupList] = useState([]);
  const scenarios = useSelector(selectScenarios);
  const searchScenarios = useSelector(selectSearchData);
  const selectedScenario = useSelector(selectSelectedScenario);
  const favoriteScenarios = useSelector(selectFavoriteScenarios);
  //
  const dispatch = useDispatch();
  //
  const modifyOptions = (options) => {
    const modifiedOptions = [];

    options?.forEach((option) => {
      if (option.isFavorite && !option?.isPredefined) {
        modifiedOptions?.push({ ...option, group: 3 });
      } else if (!option?.isPredefined) {
        modifiedOptions?.push({ ...option, group: 1 });
      }
      if (option.isFavorite && !option?.isPredefined) {
        modifiedOptions?.push({ ...option, group: 1 });
      }
      if (option?.isPredefined) {
        modifiedOptions?.push({ ...option, group: 2 });
      }
    });

    // Sort options first by group, then by label
    return modifiedOptions?.sort((a, b) => {
      if (a?.group === b?.group) {
        if (isDemoUser || (!isDemoUser && isDemoModeActivated)) {
          return Number(a?.label?.split(' ')[1]) - Number(b?.label?.split(' ')[1]);
        }
        return a?.label?.localeCompare(b?.label);
      }
      return b.group - a.group;
    });
  };
  // Update modified hotel list
  useEffect(() => {
    if (mergedHotelList?.length > 0) {
      const modifiedOptions = modifyOptions(mergedHotelList);
      setModifiedList(modifiedOptions);
    }
  }, [mergedHotelList, isDemoUser, isDemoModeActivated]);
  // Utility to check if hotel label matches the expected pattern
  const isValidHotelLabel = (ff) => {
    const hotelPattern = /^Hotel \d+$/;
    return hotelPattern.test(ff.label);
  };
  // Update addHotelGroupList based on hotelList and demo mode settings
  useEffect(() => {
    if (hotelList !== null && hotelList?.length > 0) {
      setAddHotelGroupList([...hotelList]);
      if (!isDemoUser && isDemoModeActivated && !isValidHotelLabel(hotelList[0])) {
        const demoList = getHotelDemoModeFocusOnValue(isDemoModeActivated, [...hotelList]);
        setAddHotelGroupList(demoList);
      }
      if (
        !isDemoUser &&
        !isDemoModeActivated &&
        addHotelGroupList !== null &&
        addHotelGroupList?.length > 0
      ) {
        const demoList = getHotelDemoModeFocusOnValue(isDemoModeActivated, [...addHotelGroupList]);
        setAddHotelGroupList(demoList);
      }
    }
  }, [isDemoModeActivated, hotelList]);
  //
  const selectedHotel = modifiedList?.find(
    (hotel) => hotel.name === found?.value || hotel.label === found?.value
  );
  // Handle hotel selection change: update scenario filter and close dropdown
  const handleChange = (event, value) => {
    event.stopPropagation();
    const updatedHotelName = value?.name || value?.label;
    //
    if (scenarios?.results.length) {
      const updatedScenarios = scenarios?.results.map((scenario) => {
        if (scenario.id !== filterData?.scenarioId) return scenario;
        //
        return {
          ...scenario,
          description: scenario.description.replace(found?.keyword, updatedHotelName),
          visuals: scenario.visuals.map((visual) => {
            const updatedFilters = Object.entries(visual.filters).reduce(
              (acc, [filterName, filter]) => {
                if (filter?.keyword === found?.keyword) {
                  acc[filterName] = {
                    ...filter,
                    keyword: updatedHotelName,
                    value: updatedHotelName,
                  };
                } else {
                  acc[filterName] = filter;
                }
                return acc;
              },
              {}
            );
            return {
              ...visual,
              filters: updatedFilters,
            };
          }),
        };
      });
      if (selectedScenario) {
        const updatedScenario = updatedScenarios?.find((e) => e.id === filterData?.scenarioId);
        dispatch(searchActions.setSelectedScenario(updatedScenario));
      }
      dispatch(searchActions.setScenarios({ results: updatedScenarios }));
    }
    //
    if (favoriteScenarios.length) {
      const updatedScenarios = favoriteScenarios.map((scenario) => {
        if (scenario.id !== filterData?.scenarioId) return scenario;
        //
        return {
          ...scenario,
          description: scenario.description.replace(found?.keyword, updatedHotelName),
          visuals: scenario.visuals.map((visual) => {
            const updatedFilters = Object.entries(visual.filters).reduce(
              (acc, [filterName, filter]) => {
                if (filter?.keyword === found?.keyword) {
                  acc[filterName] = {
                    ...filter,
                    keyword: updatedHotelName,
                    value: updatedHotelName,
                  };
                } else {
                  acc[filterName] = filter;
                }
                return acc;
              },
              {}
            );
            return {
              ...visual,
              filters: updatedFilters,
            };
          }),
        };
      });
      if (selectedScenario) {
        const updatedScenario = updatedScenarios?.find((e) => e.id === filterData?.scenarioId);
        dispatch(searchActions.setSelectedScenario(updatedScenario));
      }
      dispatch(searchActions.setFavoriteScenario(updatedScenarios));
    }
    //
    if (searchScenarios.length) {
      const updatedScenarios = searchScenarios.map((scenario) => {
        if (scenario.id !== filterData?.scenarioId) return scenario;
        //
        return {
          ...scenario,
          description: scenario.description.replace(found?.keyword, updatedHotelName),
          visuals: scenario.visuals.map((visual) => {
            const updatedFilters = Object.entries(visual.filters).reduce(
              (acc, [filterName, filter]) => {
                if (filter?.keyword === found?.keyword) {
                  acc[filterName] = {
                    ...filter,
                    keyword: updatedHotelName,
                    value: updatedHotelName,
                  };
                } else {
                  acc[filterName] = filter;
                }
                return acc;
              },
              {}
            );
            return {
              ...visual,
              filters: updatedFilters,
            };
          }),
        };
      });
      if (selectedScenario) {
        const updatedScenario = updatedScenarios?.find((e) => e.id === filterData?.scenarioId);
        dispatch(searchActions.setSelectedScenario(updatedScenario));
      }
      dispatch(searchActions.setSearchScenario(updatedScenarios));
    }
    handleClose();
  };
  //
  return (
    <Grid
      container
      flexDirection="row"
      justifyContent="flex-end"
      alignItems="center"
      wrap="nowrap"
      bgcolor="#ffffff"
    >
      <Autocomplete
        id="search-hotel-dropdown"
        size="small"
        sx={{
          minWidth: {
            md:
              authUser &&
              (authUser?.roles?.[0]?.name === ADMIN_ROLE ||
                authUser?.roles?.[0]?.name === SUPER_ADMIN_ROLE)
                ? 180
                : 200 ?? 200,
            lg:
              authUser &&
              (authUser?.roles?.[0]?.name === ADMIN_ROLE ||
                authUser?.roles?.[0]?.name === SUPER_ADMIN_ROLE)
                ? 280
                : 350 ?? 350,
          },
          '& .MuiOutlinedInput-root': {
            color: '#000000',
          },
          '& .MuiSvgIcon-root': {
            color: '#000000',
            width: '2rem',
            height: '2rem',
            top: 0,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#585959',
          },
          '& .MuiAutocomplete-input': {
            fontSize: { md: 13, lg: 15 },
            fontWeight: 'bold',
          },
          '& .MuiAutocomplete-endAdornment': {
            top: 'calc(50% - 0px)',
          },
        }}
        options={modifiedList ?? []}
        getOptionLabel={(option) => (option.label ? option.label : '')}
        groupBy={(option) =>
          // eslint-disable-next-line no-nested-ternary
          option.group === 3 ? 'Favorites' : option.group === 2 ? 'Predefined' : 'NonFavorites'
        }
        renderOption={(props, option) => {
          const { key, id, ...otherProps } = props;
          return (
            <Grid
              id={`hotel-id-${option?.id}`}
              key={`${option?.id}-${option?.isPredefined}`}
              container
              justifyContent="space-between"
              direction="row"
            >
              <Grid item xs={11}>
                <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...otherProps}>
                  {option.label}
                  {!option.isPredefined &&
                    groupsList?.some((group) => group.label === option?.label) && (
                      <IconButton>
                        <Tooltip title="Hotel Set">
                          <Apartment />
                        </Tooltip>
                      </IconButton>
                    )}
                  {option.isPredefined && (
                    <IconButton>
                      <Tooltip title="Hotel Set">
                        <Apartment />
                      </Tooltip>
                    </IconButton>
                  )}
                </Box>
              </Grid>
            </Grid>
          );
        }}
        renderGroup={(params) => {
          const sortedGroups = ['Favorites', 'Predefined', 'NonFavorites'].map((groupName) => {
            if (params.group === groupName && params?.children?.length > 0) {
              return (
                <Grid key={`Groups-${params.group}`}>
                  <Grid key="Favorites">
                    {groupName === 'Favorites' && params?.children?.length > 0 && (
                      <>
                        {params.children}
                        <br />
                        <Divider sx={{ bgcolor: '#61616173' }} variant="middle" />
                        <br />
                      </>
                    )}
                  </Grid>
                  <Grid key="Predefined">
                    {groupName === 'Predefined' && params?.children?.length > 0 && (
                      <>
                        <Typography paddingLeft={2} variant="subtitle2">
                          Pre-Defined Hotel Sets
                        </Typography>
                        {params.children}
                        <br />
                        <Divider sx={{ bgcolor: '#61616173' }} variant="middle" />
                        <br />
                      </>
                    )}
                  </Grid>
                  <Grid key="NonFavorites">
                    {groupName === 'NonFavorites' &&
                      params?.children?.length > 0 &&
                      params.children}
                  </Grid>
                </Grid>
              );
            }
            return null;
          });

          // Filter out null values and render sorted groups
          return sortedGroups.filter((group) => group !== null);
        }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={handleChange}
        disableClearable
        value={selectedHotel ?? []}
        renderInput={(params) => <TextField {...params} size="small" sx={{ color: '#000000' }} />}
        componentsProps={{
          paper: {
            sx: {
              minWidth: 350,
              '& .MuiAutocomplete-listbox': { overflowX: 'hidden' },
            },
          },
        }}
      />
    </Grid>
  );
};
//
export default SearchHotelDropdown;
